import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus, Edit, Trash2, ExternalLink, Star, User, Database, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import resumeAPI from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BackupControls from '../components/BackupControls';

const MyResumesPage = () => {
  const { user, isAuthenticated } = useAuth();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publicLinkId, setPublicLinkId] = useState(null);
  const [profileSlug, setProfileSlug] = useState(null);
  const [loadingPublicLink, setLoadingPublicLink] = useState(true);
  const [usersTableData, setUsersTableData] = useState(null);
  const [loadingUsersTable, setLoadingUsersTable] = useState(false);
  const [selectedExportResumeId, setSelectedExportResumeId] = useState(null);
  const [showImportedOnly, setShowImportedOnly] = useState(false);

  useEffect(() => {
    loadResumes();
    if (isAuthenticated) {
      loadPublicLinkId();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!selectedExportResumeId) return;
    const exists = resumes.some((r) => (r.resume_id || r.id || r._id) === selectedExportResumeId);
    if (!exists) {
      setSelectedExportResumeId(null);
    }
  }, [resumes, selectedExportResumeId]);

  const loadPublicLinkId = async () => {
    setLoadingPublicLink(true);
    try {
      // Fetch the user's public_link_id from their profile
      const fallbackApiUrl = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
      const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim() || fallbackApiUrl;
      if (AWS_API_BASE_URL) {
        const getAwsIdToken = () => {
          try {
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            return u?.tokens?.idToken || u?.idToken || '';
          } catch {
            return '';
          }
        };
        const token = getAwsIdToken();
        const awsUrl = (path) => {
          const base = AWS_API_BASE_URL.replace(/\/+$/, '');
          const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
          return `${base}${p}`;
        };
        const res = await fetch(awsUrl('/me/profile'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });
        if (res.ok) {
          const data = await res.json();
          const pid = data?.profile?.public_link_id || null;
          const slug = data?.profile?.profile_slug || null;
          if (slug) {
            setProfileSlug(slug);
          }
          if (pid) {
            setPublicLinkId(pid);
            console.log('Loaded public_link_id:', pid);
          } else {
            console.warn('public_link_id not found in profile response');
          }
        } else {
          console.warn('Failed to fetch profile, status:', res.status);
        }
      } else {
        console.warn('AWS_API_BASE_URL not configured');
      }
    } catch (e) {
      console.warn('Failed to load public_link_id:', e);
    } finally {
      setLoadingPublicLink(false);
    }
  };

  const loadResumes = async () => {
    setLoading(true);
    setError(null);
    try {
      // IMPORTANT: Never mutate resumes on page load.
      // Just list what the current user owns.
      const data = await (resumeAPI.fetchAllBestEffortForCurrentUser?.() || resumeAPI.fetchAll());
      const list = Array.isArray(data) ? data : [];
      
      // Fetch profile data for primary resume
      let profileData = null;
      if (isAuthenticated) {
        try {
          const fallbackApiUrl = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
          const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim() || fallbackApiUrl;
          const token = (() => {
            try {
              const u = JSON.parse(localStorage.getItem('user') || '{}');
              return u?.tokens?.idToken || u?.idToken || '';
            } catch {
              return '';
            }
          })();
          
          const awsUrl = (path) => {
            const base = AWS_API_BASE_URL.replace(/\/+$/, '');
            const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
            return `${base}${p}`;
          };
          
          const profileRes = await fetch(awsUrl('/me/profile'), {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });
          
          if (profileRes.ok) {
            const data = await profileRes.json();
            profileData = data?.profile || null;
            console.log('[MyResumes] Profile data loaded:', {
              name: profileData?.name,
              email: profileData?.email,
              photo_url: profileData?.photo_url,
              headline: profileData?.headline
            });
          }
        } catch (e) {
          console.warn('[MyResumes] Failed to load profile data:', e);
        }
      }
      
      // Merge profile data into primary resume only
      const enriched = list.map(resume => {
        if (resume.is_primary && profileData) {
          return { 
            ...resume, 
            photo_url: profileData.photo_url || resume.photo_url,
            headline: profileData.headline || resume.headline,
            name: profileData.name || resume.name,
            email: profileData.email || resume.email,
            phone: profileData.phone || resume.phone,
            location: profileData.location || resume.location,
            // Merge other profile fields
            linkedin_url: profileData.linkedin_url || resume.linkedin_url,
            website_url: profileData.website_url || resume.website_url,
            professional_summary: profileData.professional_summary || resume.professional_summary
          };
        }
        return resume;
      });
      
      const sorted = [...enriched].sort((a, b) => {
        const aPrimary = a?.is_primary === true ? 1 : 0;
        const bPrimary = b?.is_primary === true ? 1 : 0;
        if (aPrimary !== bPrimary) return bPrimary - aPrimary;
        const aTime = Date.parse(a?.updated_at || a?.created_at || 0) || 0;
        const bTime = Date.parse(b?.updated_at || b?.created_at || 0) || 0;
        return bTime - aTime;
      });
      
      setResumes(sorted);
    } catch (err) {
      setError('Failed to load resumes. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (resume) => {
    const resumeId = resume?.id || resume?._id || resume?.resume_id || resume?.resumeId;
    if (!resumeId) {
      alert('Unable to delete: Resume ID not found.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this resume?')) {
      return;
    }
    
    try {
      console.log('Deleting resume:', resumeId);
      await resumeAPI.delete(resume);
      console.log('Resume deleted successfully');
      setResumes(resumes.filter((r) => {
        const rid = r?.id || r?._id || r?.resume_id || r?.resumeId;
        // Only filter out the resume with matching resume_id, NOT by public_link_id
        // (multiple resumes can share the same public_link_id for the same user)
        return !(rid && String(rid) === String(resumeId));
      }));
      // Success feedback
      alert('Resume deleted successfully!');
    } catch (err) {
      console.error('Delete error:', err);
      const errorMessage = err?.message || 'Unknown error';
      alert(`Failed to delete resume:\n\n${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
    }
  };

  const buildPublicResumeUrl = (resumeId) => {
    return `${window.location.origin}/resume-preview/${encodeURIComponent(resumeId)}/`;
  };

  const copyPublicLink = async (resume) => {
    const resumeId = resume?.id || resume?._id || resume?.resume_id || resume?.resumeId;
    if (!resumeId) {
      alert('Resume ID not found');
      return;
    }
    
    const url = buildPublicResumeUrl(resumeId);
    
    try {
      await navigator.clipboard.writeText(url);
      alert('Public resume link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link:', error);
      alert('Failed to copy link. Please try again.');
    }
  };


  const loadUsersTableData = async () => {
    setLoadingUsersTable(true);
    try {
      const fallbackApiUrl = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
      const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim() || fallbackApiUrl;
      if (!AWS_API_BASE_URL) {
        alert('AWS backend not configured');
        return;
      }

      const getAwsIdToken = () => {
        try {
          const u = JSON.parse(localStorage.getItem('user') || '{}');
          return u?.tokens?.idToken || u?.idToken || '';
        } catch {
          return '';
        }
      };

      const token = getAwsIdToken();
      if (!token) {
        alert('Not authenticated. Please log in.');
        return;
      }

      const awsUrl = (path) => {
        const base = AWS_API_BASE_URL.replace(/\/+$/, '');
        const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
        return `${base}${p}`;
      };

      const res = await fetch(awsUrl('/me/profile'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (res.ok) {
        const data = await res.json();
        setUsersTableData(data?.profile || null);
      } else {
        alert(`Failed to load Users table data: ${res.status}`);
      }
    } catch (error) {
      console.error('Error loading Users table data:', error);
      alert(`Error: ${error?.message || 'Failed to load Users table data'}`);
    } finally {
      setLoadingUsersTable(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your resumes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadResumes} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const importedResumes = resumes.filter((r) => r.import_source === 'file');
  const createdResumes = resumes.filter((r) => r.import_source !== 'file');

  const renderResumeCards = (list) => {
    if (list.length === 0) {
      return (
        <div className="text-sm text-gray-500">No resumes in this section yet.</div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {list.map((resume) => {
          const resumeId = resume.id || resume._id || resume.resume_id || resume.resumeId;
          return (
            <div
              key={resumeId}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
            >
              {/* Preview Image or Placeholder */}
              <div className="h-32 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center relative">
                {/* QR Code - Left side */}
                <div className="absolute left-3 top-1/2 -translate-y-1/2 bg-white p-1.5 rounded-lg shadow-md" title="Scan to view resume">
                  <QRCodeSVG
                    value={buildPublicResumeUrl(resumeId)}
                    size={64}
                    level="M"
                    includeMargin={false}
                  />
                </div>
                
                {/* Resume Photo/Icon - Center */}
                {resume.photo_url ? (
                  <img
                    src={resume.photo_url}
                    alt={resume.title}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                ) : (
                  <FileText className="w-12 h-12 text-primary-600" />
                )}
                
                {/* Primary badge - Right side, below export checkbox */}
                {resume.is_primary && (
                  <div className="absolute top-14 right-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 z-10">
                    <Star className="w-3 h-3" />
                    Primary
                  </div>
                )}
                
                {/* Export checkbox - Top right */}
                <label
                  className="absolute top-2 right-2 inline-flex items-center gap-1 bg-white/90 border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-700 cursor-pointer shadow-sm z-10"
                  title="Select this resume for export"
                >
                  <input
                    type="checkbox"
                    checked={selectedExportResumeId === resumeId}
                    onChange={() => {
                      setSelectedExportResumeId((prev) => (prev === resumeId ? null : resumeId));
                    }}
                    className="h-4 w-4 text-primary-600 rounded"
                  />
                  Select export file
                </label>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-1">
                  {resume.title}
                </h3>
                {resume.headline && (
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {resume.headline}
                  </p>
                )}
                {resume.target_role && (
                  <p className="text-sm text-primary-600 font-medium mb-3">
                    {resume.target_role}
                  </p>
                )}

                {/* Stats */}
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <span>Template: {resume.template || 'Modern'}</span>
                  {resume.ai_score > 0 && (
                    <span className="font-semibold text-green-600">
                      AI Score: {resume.ai_score}/100
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Link
                    to={`/builder/${resumeId}`}
                    className="flex-1 btn btn-primary text-sm inline-flex items-center justify-center space-x-1"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit</span>
                  </Link>
                  <button
                    onClick={() => copyPublicLink(resume)}
                    className="btn btn-outline text-sm p-2"
                    title="Copy public resume link"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  {!resume.is_primary && (
                    <button
                      onClick={() => handleDelete(resume)}
                      className="btn btn-secondary text-sm p-2 hover:bg-red-100 hover:text-red-600"
                      title="Delete resume"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-bold text-gray-900">My Resumes</h1>
              <Link
                to={
                  isAuthenticated
                    ? (profileSlug || publicLinkId
                      ? `/profile/view?public=${encodeURIComponent(profileSlug || publicLinkId)}`
                      : '/profile/view')
                    : '/profile/view'
                }
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors shadow-md hover:shadow-lg font-medium"
                title="View Your Profile"
              >
                <User className="w-5 h-5" />
                <span>View Profile</span>
              </Link>
              {isAuthenticated && (
                <>
                  {/* View User Table button hidden per user request */}
                </>
              )}
            </div>
            <p className="text-gray-600">
              Manage and edit your resume collection
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImportedOnly(!showImportedOnly)}
              className={`btn inline-flex items-center space-x-2 ${
                showImportedOnly 
                  ? 'btn-primary' 
                  : 'btn-outline border-2 border-primary-600 text-primary-600 hover:bg-primary-50'
              }`}
            >
              <Database className="w-5 h-5" />
              <span>{showImportedOnly ? 'Show All Resumes' : 'Show Imported Only'}</span>
            </button>
            <Link to="/builder" className="btn btn-primary inline-flex items-center space-x-2">
              <Plus className="w-5 h-5" />
              <span>Create New Resume</span>
            </Link>
          </div>
        </div>

        {/* Backup & Sync Controls */}
        {isAuthenticated && (
          <BackupControls
            selectedResumeId={selectedExportResumeId}
            showImport={false}
          />
        )}

        {/* Resumes Sections */}
        {resumes.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              No resumes yet
            </h2>
            <p className="text-gray-600 mb-6">
              Create your first professional resume to get started
            </p>
            <Link to="/builder" className="btn btn-primary inline-flex items-center space-x-2">
              <Plus className="w-5 h-5" />
              <span>Create Your First Resume</span>
            </Link>
          </div>
        ) : showImportedOnly ? (
          <div className="space-y-10">
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">Imported Resumes</h2>
                  <p className="text-sm text-gray-600">
                    Resumes uploaded from Word or PDF ({importedResumes.length})
                  </p>
                </div>
              </div>
              {isAuthenticated && (
                <BackupControls
                  selectedResumeId={selectedExportResumeId}
                  showExport={false}
                  showAutoSave={false}
                  showHeader={false}
                />
              )}
              {renderResumeCards(importedResumes)}
            </div>
          </div>
        ) : (
          <div className="space-y-10">
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">Created Resumes</h2>
                  <p className="text-sm text-gray-600">
                    Resumes created in the website ({createdResumes.length})
                  </p>
                </div>
              </div>
              {renderResumeCards(createdResumes)}
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">Imported Resumes</h2>
                  <p className="text-sm text-gray-600">
                    Resumes uploaded from Word or PDF ({importedResumes.length})
                  </p>
                </div>
              </div>
              {isAuthenticated && (
                <BackupControls
                  selectedResumeId={selectedExportResumeId}
                  showExport={false}
                  showAutoSave={false}
                  showHeader={false}
                />
              )}
              {renderResumeCards(importedResumes)}
            </div>
          </div>
        )}

        {/* Users Table Data Modal */}
        {usersTableData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setUsersTableData(null)}>
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Database className="w-6 h-6 text-green-600" />
                  Users Table Data
                </h2>
                <button
                  onClick={() => setUsersTableData(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto">
                  {JSON.stringify(usersTableData, null, 2)}
                </pre>
              </div>
              <div className="p-6 border-t bg-gray-50">
                <button
                  onClick={() => setUsersTableData(null)}
                  className="btn btn-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyResumesPage;
